import { tenantHeaders } from './tenantSession';
// DR-017 Business Web MFA client — pure state/validation logic + DI'd fetch helpers (TOTP second factor).
//
// Security: never persists the enrolment secret / otpauth URI / recovery codes; maps structured backend
// states (never raw errors); ONLY a conclusively invalid/revoked FIRST factor triggers logout — no MFA
// state does. Token getter is injected so this module imports cleanly (and is unit-testable) without the
// Firebase SDK. Assurance lives server-side (DB), so an ordinary Firebase token refresh preserves it.

export const MFA_STATES = {
  NOT_REQUIRED: 'MFA_NOT_REQUIRED',
  GRACE_PERIOD: 'MFA_GRACE_PERIOD',
  ENROLMENT_REQUIRED: 'MFA_EMAIL_ENROLMENT_REQUIRED', // backend enrolment-required code (email == recovery)
  ENROLMENT_REQUIRED_ALT: 'MFA_ENROLMENT_REQUIRED',
  CHALLENGE_REQUIRED: 'MFA_CHALLENGE_REQUIRED',
  STEP_UP_REQUIRED: 'MFA_STEP_UP_REQUIRED',
  ASSURANCE_VALID: 'MFA_ASSURANCE_VALID',
  ACCESS_RESTRICTED: 'MFA_ACCESS_RESTRICTED',
  ASSURANCE_EXPIRED: 'MFA_ASSURANCE_EXPIRED',
  INVALID_CODE: 'MFA_INVALID_CODE',
  CODE_REPLAYED: 'MFA_CODE_REPLAYED',
  ATTEMPTS_EXCEEDED: 'MFA_ATTEMPTS_EXCEEDED',
  FACTOR_REVOKED: 'MFA_FACTOR_REVOKED',
  POLICY_CHANGED: 'MFA_POLICY_CHANGED',
  RECOVERY_REQUIRED: 'MFA_RECOVERY_REQUIRED',
};

// Friendly, non-raw messages per state. Never surfaces backend exception text or internal ids.
const _MESSAGES = {
  MFA_ENROLMENT_REQUIRED: 'Set up your authenticator app to continue.',
  MFA_EMAIL_ENROLMENT_REQUIRED: 'Set up your authenticator app to continue.',
  MFA_CHALLENGE_REQUIRED: 'Enter the 6-digit code from your authenticator app.',
  MFA_STEP_UP_REQUIRED: 'Confirm your identity with a 6-digit code to continue.',
  MFA_ASSURANCE_EXPIRED: 'Your verification expired. Enter a new 6-digit code.',
  MFA_INVALID_CODE: 'That code is incorrect. Try the current code.',
  MFA_CODE_REPLAYED: 'That code was already used. Wait for the next one.',
  MFA_ATTEMPTS_EXCEEDED: 'Too many attempts. Please wait a moment and try again.',
  MFA_FACTOR_REVOKED: 'Your authenticator was reset. Set it up again.',
  MFA_POLICY_CHANGED: 'Your security settings changed. Please verify again.',
  MFA_RECOVERY_REQUIRED: 'Use a recovery code to regain access.',
  MFA_ACCESS_RESTRICTED: 'Access is restricted by your organization security policy.',
  MFA_GRACE_PERIOD: 'Multi-factor authentication will soon be required — set it up now.',
};

export function mfaMessage(state) {
  return _MESSAGES[state] || 'Additional verification is required.';
}

// Extract the structured MFA state from a backend detail payload (string or {state}).
export function extractMfaState(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return _MESSAGES[detail] ? detail : (Object.values(MFA_STATES).includes(detail) ? detail : null);
  if (typeof detail === 'object') return detail.state || null;
  return null;
}

// Only a conclusively invalid/revoked FIRST factor logs out. No MFA_* state ever triggers logout.
export function isLogoutState(state) {
  return false; // MFA states are handled in-flow; first-factor auth errors are handled by the auth layer.
}

export function isValidOtp(code) {
  return typeof code === 'string' && /^\d{6}$/.test(code.trim());
}

// ── DI'd fetch helpers: `getToken` returns a fresh Firebase ID token (DR-013 compatible). ──
function _url(path) {
  // Use the SAME canonical backend base the rest of the Business app uses (CRMAuthContext line 37).
  // NB: the previous `typeof process !== 'undefined'` runtime guard is FALSE in the browser (there is
  // no global `process`), which short-circuited base to '' and sent MFA POSTs to the Web origin
  // (app-staging) → 405. CRA statically inlines REACT_APP_* at build time, so read them directly.
  const base = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';
  return `${base}/api/v1${path}`;
}

async function _post(path, body, getToken) {
  const token = await getToken();
  // MFA policy is TENANT-governed (DR-017): /mfa/status and /mfa/policy resolve the caller's
  // membership and read that tenant's policy. Neither carries a path tenant, so the active-tenant
  // header is what makes them deterministic for a person holding several memberships.
  const res = await fetch(_url(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = {}; }
  return { ok: res.ok, status: res.status, data, state: extractMfaState(data && data.detail) };
}

export const mfaApi = {
  status: (getToken) => _post('/mfa/status', {}, getToken),
  enrollStart: (getToken) => _post('/mfa/enroll/start', {}, getToken),
  enrollVerify: (challengeId, code, getToken) => _post('/mfa/enroll/verify', { challenge_id: challengeId, code }, getToken),
  verify: (code, purpose, getToken) => _post('/mfa/verify', { code, purpose: purpose || 'login' }, getToken),
  recoveryVerify: (code, getToken) => _post('/mfa/recovery/verify', { code }, getToken),
  recoveryRegenerate: (getToken) => _post('/mfa/recovery/regenerate', {}, getToken),
  factorReset: (getToken) => _post('/mfa/factor/reset', {}, getToken),
};

// Run a sensitive mutation; if the backend returns MFA_STEP_UP_REQUIRED, verify a step-up code then
// retry the mutation EXACTLY ONCE (no duplicate mutation on success, no endless retry).
export async function withStepUpRetry(mutationFn, { verifyCode, getToken } = {}) {
  const first = await mutationFn();
  const state = first && (first.state || extractMfaState(first.data && first.data.detail));
  if (first && first.status === 403 && state === MFA_STATES.STEP_UP_REQUIRED && verifyCode) {
    const stepped = await mfaApi.verify(verifyCode, 'step_up', getToken);
    if (!stepped.ok) return { ...stepped, steppedUp: false };
    const retried = await mutationFn();      // retried exactly once after successful step-up
    return { ...retried, steppedUp: true, retriedOnce: true };
  }
  return first;
}
